import type { UpdateAppetiteMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAppetiteDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { UpdateAppetiteInput } from './types';

export const useUpdateAppetite = createMutationHook({
  name: 'updateAppetite',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.appetite.update.mutationOptions({ onSuccess }),
  mapInput: (variables: UpdateAppetiteInput): UpdateAppetiteInput => {
    const { AppetiteType, ...rest } = variables;
    if (!AppetiteType) {
      throw new Error('AppetiteType is required for appetite update');
    }

    return {
      // `OriginalTimestamp` rides along: the legacy mutation pins it in its
      // `where`, so dropping it here is exactly the V3 lock loss COI-622 is
      // closing.
      ...rest,
      AppetiteType,
      CustomAttributeData: rest.CustomAttributeData ?? null,
    };
  },
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed `getAppetites`
    // (the link-item modal's picker), which nothing in the repo invalidated.
    trpc.frontend.appetite.pathKey(),
    // COI-471 parity: Apollo twin evicts the `risk` field (appetites
    // surface on the linked risk). Both siblings carry this key.
    trpc.frontend.risk.pathKey(),
  ],
  mapResponse: (): UpdateAppetiteMutation => ({
    update_appetite: {
      __typename: 'appetite_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateAppetiteDocument,
  graphqlVariables: (variables: UpdateAppetiteInput) => ({
    ...variables,
  }),
  evictFields: ['appetite'],
});
